import { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { SettingOutlined } from '@ant-design/icons';
import moment from 'moment';
import { getQuiz, changeParam } from 'store/quiz';
import TableList from 'components/common/TableList';
import TablePagination from 'components/common/TablePagenation';
import TableSearch from 'components/common/TableSearch';
import QuizSettings from 'components/branch/quiz/QuizSettings';

export default function PhishingPageList(props: any) {
  let tableRowCount = Math.trunc((window.innerHeight - 315) / 48);
  tableRowCount = tableRowCount < 5 ? 5 : tableRowCount;

  const { formatMessage } = useIntl();
  const [loading, setLoding] = useState(false);
  const [pageSize, setPageSize] = useState(tableRowCount);
  const [currentPage, setCurrentPage] = useState(0);
  const dispatch = useDispatch();
  const { sort, filter } = useSelector(
    (state: any) => ({
      sort: state.quiz.param.sort,
      filter: state.quiz.param.filter,
    }),
    shallowEqual,
  );
  const needReload = useSelector((state: any) => state.quiz.needReload);
  const respData = useSelector((state: any) => state.quiz.data);
  const totalCount = useSelector((state: any) => state.quiz.totalCount);
  const totalPages = useSelector((state: any) => state.quiz.totalPages);
  const isAdmin = useSelector((state: any) => state.login.userData.isAdmin);

  useEffect(() => {
    getQuizData();
  }, [sort, filter, pageSize, currentPage, needReload]);

  // API 호출
  const getQuizData = async () => {
    if (!loading) {
      setLoding(true);

      try {
        const params = {
          filter: JSON.stringify(filter),
          sort: JSON.stringify(sort),
          offset: pageSize * (currentPage < 0 ? 0 : currentPage), // page 는 0 번 부터 시작
          limit: pageSize < 1 ? 20 : pageSize,
        };

        await dispatch(getQuiz(params));
      } catch (e) {
        console.log(e);
      }
    }

    setLoding(false);
  };

  // 검색
  const searchOption = {
    searchAll: formatMessage({ id: 'Faq_1', defaultMessage: '전체' }),
    quizContent: formatMessage({ id: 'Edu_33', defaultMessage: '퀴즈 내용' }),
    fileName: formatMessage({ id: 'Edu_38', defaultMessage: '교육 영상' }),
  };

  // 필터
  const filterOption: { [key: string]: any } = {};

  // 테이블
  const baseColumns = [
    {
      Header: <SettingOutlined />,
      accessor: 'setting',
      resizable: false,
      sortable: false,
      width: 130,
      headerClassName: 'react-table-th',
      className: 'react-table-td text-align-center settings',
      Cell: function cellFunc(props: { [key: string]: any }) {
        return <QuizSettings isAdmin={isAdmin} dataInfo={props.row.original} />;
      },
    },
    {
      Header: 'No',
      accessor: 'quizNo',
      resizable: false,
      sortable: true,
      width: 70,
      headerClassName: 'react-table-th',
      className: 'react-table-td text-align-center number-text',
      Cell: (props: { [key: string]: any }) => {
        let printValue = '-';
        if (props.value) {
          printValue = props.value;
        }
        return printValue;
      },
    },
    {
      Header: formatMessage({ id: 'Edu_33', defaultMessage: '퀴즈 내용' }),
      accessor: 'quizContent',
      resizable: false,
      sortable: false,
      width: 300,
      headerClassName: 'react-table-th',
      className: 'react-table-td',
      Cell: function content(props: { [key: string]: any }) {
        let printValue = '-';
        if (props.value) {
          printValue = props.value;
        }
        return (
          <div className="ellipsis" title={printValue}>
            {printValue}
          </div>
        );
      },
    },
    {
      Header: formatMessage({ id: 'Edu_38', defaultMessage: '교육 영상' }),
      accessor: 'fileName',
      resizable: false,
      sortable: false,
      width: 300,
      headerClassName: 'react-table-th',
      className: 'react-table-td',
      Cell: function file(props: { [key: string]: any }) {
        let printValue = '-';
        if (props.value) {
          printValue = props.value;
        }
        return (
          <div className="ellipsis" title={printValue}>
            {printValue}
          </div>
        );
      },
    },
    {
      Header: formatMessage({ id: 'Edu_35', defaultMessage: '정답' }),
      accessor: 'quizSolution',
      resizable: false,
      sortable: false,
      width: 80,
      headerClassName: 'react-table-th',
      className: 'react-table-td text-align-center',
      Cell: (props: { [key: string]: any }) => {
        let printValue = '-';
        if (props.value) {
          printValue = props.value === 1 ? 'O' : 'X';
        }
        return printValue;
      },
    },
    {
      Header: formatMessage({ id: 'Date_20', defaultMessage: '수정일' }),
      accessor: 'modifyEpoch',
      resizable: false,
      sortable: true,
      width: 120,
      headerClassName: 'react-table-th overflow-visible',
      className: 'react-table-td text-align-center epoch',
      Cell: function regEpoch(props: { [key: string]: any }) {
        const { value } = props;
        const dateFormat = localStorage.getItem('language') === 'en' ? 'MM/DD/YYYY' : 'YYYY-MM-DD';
        return <div>{moment.unix(value).format(`${dateFormat} HH:mm:ss`)}</div>;
      },
    },
    {
      Header: formatMessage({ id: 'Date_23', defaultMessage: '생성일' }),
      accessor: 'regEpoch',
      resizable: false,
      sortable: true,
      width: 120,
      headerClassName: 'react-table-th overflow-visible',
      className: 'react-table-td text-align-center epoch',
      Cell: function regEpoch(props: { [key: string]: any }) {
        const { value } = props;
        const dateFormat = localStorage.getItem('language') === 'en' ? 'MM/DD/YYYY' : 'YYYY-MM-DD';
        return <div>{moment.unix(value).format(`${dateFormat} HH:mm:ss`)}</div>;
      },
    },
  ];

  const columns: Array<any> = useMemo(() => baseColumns, [respData]);

  return (
    <>
      <TableSearch
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalCount={totalCount}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        changeParam={changeParam}
        searchOption={searchOption}
        filter={filter}
        filterOption={filterOption}
      />
      <TableList
        columns={columns}
        data={respData.success}
        changeParam={changeParam}
        loading={loading}
        disableKey="quizEnabled"
      />
      <TablePagination
        totalPages={totalPages}
        currentPage={currentPage + 1}
        setCurrentPage={setCurrentPage}
      />
    </>
  );
}
